import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Trash2 } from 'lucide-react';

interface Comment {
  id: string;
  announcement_id: string;
  user_id: string;
  content: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    email: string;
  };
}

interface Props {
  announcementId: string;
}

export default function AnnouncementCommentSection({ announcementId }: Props) {
  const { user } = useAuthStore();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 댓글 로드
  useEffect(() => {
    fetchComments();
  }, [announcementId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/v1/announcements/${announcementId}/comments`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to fetch comments');

      const data = await response.json();
      setComments(data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  // 댓글 작성
  const handleSubmitComment = async () => {
    if (!user) {
      setMessage({ type: 'error', text: '로그인이 필요합니다.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (!newComment.trim()) {
      setMessage({ type: 'error', text: '댓글 내용을 입력해주세요.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const response = await fetch('/api/v1/announcement-comments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          announcement_id: announcementId,
          content: newComment,
        }),
      });

      if (!response.ok) throw new Error('Failed to create comment');

      setNewComment('');
      setMessage({ type: 'success', text: '댓글이 등록되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
      await fetchComments();
    } catch (error) {
      console.error('Failed to create comment:', error);
      setMessage({ type: 'error', text: '댓글 작성에 실패했습니다.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 대댓글 작성
  const handleSubmitReply = async (parentId: string) => {
    if (!user) {
      setMessage({ type: 'error', text: '로그인이 필요합니다.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (!replyContent.trim()) {
      setMessage({ type: 'error', text: '댓글 내용을 입력해주세요.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      const response = await fetch('/api/v1/announcement-comments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          announcement_id: announcementId,
          content: replyContent,
          parent_id: parentId,
        }),
      });

      if (!response.ok) throw new Error('Failed to create reply');

      setReplyingTo(null);
      setReplyContent('');
      setMessage({ type: 'success', text: '답글이 등록되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
      await fetchComments();
    } catch (error) {
      console.error('Failed to create reply:', error);
      setMessage({ type: 'error', text: '답글 작성에 실패했습니다.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/v1/announcement-comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete comment');

      setMessage({ type: 'success', text: '댓글이 삭제되었습니다.' });
      setTimeout(() => setMessage(null), 3000);
      await fetchComments();
    } catch (error) {
      console.error('Failed to delete comment:', error);
      setMessage({ type: 'error', text: '댓글 삭제에 실패했습니다.' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 계층 구조 구성
  const topLevelComments = comments.filter(c => !c.parent_id);
  const getReplies = (parentId: string) =>
    comments.filter(c => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="mt-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="mt-6 border-t border-gray-200 pt-6">
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <h4 className="font-semibold text-gray-900 mb-4">
        댓글 {comments.length}개
      </h4>

      {/* 댓글 작성 */}
      {user ? (
        <div className="mb-6">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="댓글을 입력하세요..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          <button
            onClick={handleSubmitComment}
            className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
          >
            댓글 작성
          </button>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-sm text-gray-600">댓글을 작성하려면 로그인해주세요.</p>
        </div>
      )}

      {/* 댓글 목록 */}
      <div className="space-y-4">
        {topLevelComments.length > 0 ? (
          topLevelComments.map((comment) => (
            <div key={comment.id} className="border-l-2 border-gray-200 pl-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="font-medium text-gray-900 text-sm">
                    {comment.user.email}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {new Date(comment.created_at).toLocaleString('ko-KR')}
                  </span>
                </div>
                <div className="flex gap-2">
                  {user && (
                    <>
                      <button
                        onClick={() => setReplyingTo(comment.id)}
                        className="text-xs text-primary hover:underline"
                      >
                        답글
                      </button>
                      {user.id === comment.user_id && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-xs text-red-600 hover:underline flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          삭제
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <p className="text-gray-700 text-sm whitespace-pre-line">
                {comment.content}
              </p>

              {/* 대댓글 작성 */}
              {replyingTo === comment.id && (
                <div className="mt-3 ml-4">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="답글을 입력하세요..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => handleSubmitReply(comment.id)}
                      className="px-3 py-1 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      답글 작성
                    </button>
                    <button
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent('');
                      }}
                      className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      취소
                    </button>
                  </div>
                </div>
              )}

              {/* 대댓글 목록 */}
              {getReplies(comment.id).map((reply) => (
                <div key={reply.id} className="mt-3 ml-4 pl-4 border-l border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900 text-sm">
                        {reply.user.email}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        {new Date(reply.created_at).toLocaleString('ko-KR')}
                      </span>
                    </div>
                    {user && user.id === reply.user_id && (
                      <button
                        onClick={() => handleDeleteComment(reply.id)}
                        className="text-xs text-red-600 hover:underline flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        삭제
                      </button>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm whitespace-pre-line">
                    {reply.content}
                  </p>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            첫 번째 댓글을 작성해보세요.
          </div>
        )}
      </div>
    </div>
  );
}
